import React, { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Typography, type SxProps, type Theme } from '@mui/material';

// Define all available logo variants for the application
export const logoVariants = {
  primary: {
    light: '/images/Mattoboard logo 215x215.png',
    dark: '/images/Mattoboard logo 215x215.png',  // Same for now, could be replaced with a dark-mode specific logo
  },
  // Add more logo variants as needed (e.g., monochrome, simplified, etc.)
  monochrome: {
    light: '/images/Mattoboard logo 215x215.png',  // Could be replaced with monochrome versions
    dark: '/images/Mattoboard logo 215x215.png',
  }
};

// Logo component props with flexible options
export interface LogoProps {
  /**
   * The size of the logo
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';
  
  /**
   * Whether to display the text "Mattoboard" next to the logo
   * @default true
   */
  withText?: boolean;
  
  /**
   * The variant of the logo to use
   * @default 'primary'
   */
  variant?: keyof typeof logoVariants;
  
  /**
   * Override the coloring of the text
   * If not specified, text color will adapt to current theme
   */
  textColor?: string;
  
  /**
   * Additional styling for the container
   */
  sx?: SxProps<Theme>;
  
  /**
   * Additional classname for styling
   */
  className?: string;
  
  /**
   * Force a specific theme mode
   * Useful when the logo needs to be displayed against a specific background
   * regardless of the global theme
   */
  forceMode?: 'light' | 'dark';
}

/**
 * Mattoboard logo component that adapts to the current theme
 * 
 * This component provides a consistent way to display the Mattoboard logo
 * throughout the application, with various size and style options.
 * 
 * It automatically adapts to the current theme (light/dark) and can be
 * configured to show just the logo, or the logo with text.
 */
export const Logo: React.FC<LogoProps> = ({ 
  size = 'medium',
  withText = true,
  variant = 'primary',
  textColor,
  sx = {},
  className,
  forceMode
}) => {
  const theme = useTheme();
  
  // Determine if we're in dark mode based on theme or forced mode
  const isDarkMode = forceMode ? forceMode === 'dark' : theme.palette.mode === 'dark';
  
  // Size mappings for logo and text
  const sizeMap = {
    small: { logoSize: 32, fontSize: '1rem', spacing: 1 },
    medium: { logoSize: 48, fontSize: '1.25rem', spacing: 1.5 },
    large: { logoSize: 64, fontSize: '1.75rem', spacing: 2 }
  };
  
  const { logoSize, fontSize, spacing } = sizeMap[size];
  
  // Select the appropriate logo based on theme and variant
  const logoPath = useMemo(() => {
    return isDarkMode 
      ? logoVariants[variant].dark 
      : logoVariants[variant].light;
  }, [isDarkMode, variant]);
  
  // Determine text color based on props or theme
  const finalTextColor = useMemo(() => {
    if (textColor) return textColor;
    return isDarkMode ? theme.palette.common.white : theme.palette.text.primary;
  }, [isDarkMode, textColor, theme]);
  
  return (
    <Box 
      sx={{ 
        display: 'flex', 
        alignItems: 'center',
        gap: spacing,
        ...sx
      }}
      className={className}
    >
      {/* Logo image - automatically adapts to theme */}
      <img 
        src={logoPath} 
        alt="Mattoboard Logo" 
        style={{
          width: logoSize,
          height: logoSize,
          objectFit: 'contain',
          filter: isDarkMode ? 'brightness(1.2)' : 'none' // Subtle enhancement for dark mode
        }}
      />
      
      {/* Optional text logo */}
      {withText && (
        <Typography
          variant="h6"
          component="span"
          sx={{
            fontWeight: 'bold',
            fontSize,
            color: finalTextColor,
            fontFamily: '"Inter", sans-serif',
            letterSpacing: '-0.02em'
          }}
        >
          Mattoboard
        </Typography>
      )}
    </Box>
  );
};

// Default export
export default Logo;